/**
 * Amplifier profiles -- group 00 (reference exemplars).
 *
 * Each topology gets a genuinely different nonlinear model, dynamic model and
 * output-impedance behaviour; the kit picks those from `topology` so a 300B SET
 * and an NCore module do not share an algorithm (spec 19).
 */

import type { AmpProfile } from '../../types/hardware';
import { HARMONIC_SHAPES, bandwidthResponse, makeAmp, usd } from '../kit';

export const AMPS_00: AmpProfile[] = [
  makeAmp({
    mfr: 'Woo Audio',
    model: 'GES',
    price: usd(1890, 'ORIGINAL_RETAIL', 2015, 'Discontinued; original US retail. Named in the spec as the default amplifier.'),
    year: 2009,
    country: 'United States',
    topology: 'tube-otl',
    form: 'electrostatic-energiser',
    devices: '2 x 6SN7 driver, 2 x 6S4A output, transformerless direct drive',
    power: { biasVolts: 580, outputVrms: 320, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 54,
    thdPercent: 0.35,
    snrDb: 92,
    bandwidth: [7, 42000],
    outputImpedanceOhms: 2200,
    feedbackDb: 4,
    outputTransformer: false,
    electrostaticDrive: true,
    estatBias: 'pro-580v',
    crosstalkDb: -66,
    harmonicShape: HARMONIC_SHAPES.otl,
    nonlinear: { kind: 'triode', amount: 0.62, asymmetry: 0.45, bias: 0.55, headroomDb: 11, oversample: 8 },
    dynamics: { sagDb: 1.3, sagTimeMs: 210, recoveryMs: 480, slewRateVus: 22, headroomDb: 1.5 },
    clipping: { kind: 'soft', kneeDb: 8, stickiness: 0.3, onsetDbFS: -1 },
    confidence: 'ESTIMATED',
    accent: '#e0b070',
    tags: ['electrostatic', 'energiser', 'otl', 'default-system', 'stax-pro-bias'],
    notes:
      'Direct-drive electrostatic energiser: the tubes swing several hundred volts straight onto the stator, with no output transformer. Modelled with an OTL-style harmonic structure (H3-dominant with a substantial H2 component), meaningful supply sag on sustained peaks, and a very high source impedance driving what is essentially a capacitive load.',
    provenanceNote:
      'Bias standard and tube complement from Woo Audio published material. Distortion, noise and bandwidth are estimates typical of low-feedback OTL electrostatic energisers; Woo Audio does not publish full specifications for the GES.',
  }),

  makeAmp({
    mfr: 'Pass Laboratories',
    model: 'XA25',
    price: usd(5250, 'MSRP', 2023),
    year: 2017,
    country: 'United States',
    topology: 'solid-state-a',
    form: 'stereo-power',
    devices: 'Single-ended-biased complementary output, no cascode, low feedback',
    power: { watts8: 25, watts4: 50, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 20,
    thdPercent: 0.05,
    snrDb: 105,
    bandwidth: [1.5, 100000],
    dampingFactor: 150,
    feedbackDb: 6,
    crosstalkDb: -92,
    harmonicShape: HARMONIC_SHAPES.classA,
    nonlinear: { kind: 'jfet-square', amount: 0.55, asymmetry: 0.55, bias: 0.7, headroomDb: 11, oversample: 4 },
    dynamics: { sagDb: 0.35, sagTimeMs: 45, recoveryMs: 120, thermalDb: 0.4, thermalTimeMs: 8000, slewRateVus: 50, headroomDb: 1.8 },
    clipping: { kind: 'asymmetric', kneeDb: 4.5, stickiness: 0.12 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7fd4c1',
    tags: ['class-a', 'low-feedback', 'single-ended-bias'],
    notes:
      'Low-feedback Class A design whose distortion is dominated by a smoothly rising second harmonic. The modelled transfer curve is deliberately asymmetric: distortion grows with level rather than sitting at a fixed low floor, and it does not collapse into higher-order products near clipping.',
    provenanceNote: 'Power, damping factor and bandwidth from Pass Laboratories published specifications.',
  }),

  makeAmp({
    mfr: 'Audio Note',
    model: 'Ongaku',
    price: usd(180000, 'MSRP', 2023),
    year: 1989,
    country: 'United Kingdom',
    topology: 'tube-set',
    form: 'integrated',
    devices: '211 directly-heated triode SET, silver output transformers, zero feedback',
    power: { watts8: 27, watts4: 18, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 34,
    thdPercent: 1.2,
    snrDb: 84,
    bandwidth: [14, 32000],
    outputImpedanceOhms: 2.6,
    feedbackDb: 0,
    outputTransformer: true,
    transformerLfHz: 16,
    transformerHfHz: 30000,
    crosstalkDb: -58,
    harmonicShape: HARMONIC_SHAPES.set,
    nonlinear: { kind: 'triode', amount: 0.95, asymmetry: 0.85, bias: 0.9, headroomDb: 7, oversample: 8 },
    dynamics: { sagDb: 2.1, sagTimeMs: 240, recoveryMs: 600, slewRateVus: 14, headroomDb: 0.9 },
    clipping: { kind: 'soft', kneeDb: 9, stickiness: 0.4 },
    confidence: 'ESTIMATED',
    accent: '#d4a05a',
    tags: ['set', '211', 'zero-feedback', 'silver-transformer', 'flagship'],
    notes:
      'Zero-feedback 211 single-ended triode. The model applies a strongly second-harmonic-biased transfer curve whose distortion rises roughly 1 dB per dB of drive, a ~2.6 ohm output impedance that lets the loudspeaker impedance curve print itself onto the frequency response, and transformer core saturation at low frequencies.',
    provenanceNote:
      'Power and topology from Audio Note published material. THD and bandwidth are estimates typical of zero-feedback 211 SET designs.',
  }),

  makeAmp({
    mfr: 'Mola Mola',
    model: 'Kaluga',
    price: usd(19500, 'MSRP', 2023, 'Price per monoblock pair.'),
    year: 2015,
    country: 'Netherlands',
    topology: 'class-d',
    form: 'monoblock',
    devices: 'Bruno Putzeys Class D output with Ncore-derived feedback loop',
    power: { watts8: 400, watts4: 700, confidence: 'MANUFACTURER_SPEC' },
    gainDb: 26,
    thdPercent: 0.0007,
    snrDb: 128,
    bandwidth: [1, 50000],
    dampingFactor: 1000,
    feedbackDb: 32,
    crosstalkDb: -120,
    harmonicShape: HARMONIC_SHAPES.classD,
    nonlinear: { kind: 'class-d-pwm', amount: 0.28, carrierHz: 500000, headroomDb: 7, asymmetry: 0.06, oversample: 4 },
    dynamics: { sagDb: 0.08, sagTimeMs: 25, recoveryMs: 60, slewRateVus: 30, headroomDb: 0.6 },
    clipping: { kind: 'hard', kneeDb: 0.3, stickiness: 0.05 },
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6ea8ff',
    tags: ['class-d', 'high-damping', 'monoblock'],
    notes:
      'Very high loop gain switching amplifier. Modelled with an essentially vanishing distortion floor that is odd-order when it appears, a 0.008 ohm source impedance that flattens loudspeaker impedance interaction almost completely, and an abrupt clipping characteristic with no soft knee.',
    provenanceNote: 'Power, THD, SNR and damping factor from Mola Mola published specifications.',
  }),
];
